<script setup lang="ts">
import { ref, computed } from 'vue'
import type { NavigationDrawerProps } from '@ui/models/navigation/NavigationDrawer.model'
import AppLogo from '@ui/components/chargecloud/AppLogo.vue'
import AppUserMenu from '@ui/components/navigation/AppUserMenu.vue'
import AppNavigationItems from '@ui/components/navigation/AppNavigationItems.vue'
import AppIcon from '@ui/components/elements/AppIcon.vue'
import { useLocalePath } from '#i18n'
import { onClickOutside, useEventListener } from '@vueuse/core'
import { useKeyboardNavigation } from '@ui/composables/useKeyboardNavigation'
import { useTenantLogo } from '@ui/composables/useTenantLogo'

const {
  isCollapsed = false,
  navigationItems = [],
  userMenuItems = [],
  footerLinks = [],
  companyName,
  userName,
  userMail,
  userImage,
} = defineProps<NavigationDrawerProps>()

const emit = defineEmits<{
  'update:isCollapsed': [isCollapsed: boolean]
}>()

const navigationContainerRef = ref<HTMLElement | null>(null)
const navigationItemsRef = ref<{ handleCloseSubmenu: () => void } | null>(null)
const desktopUserMenuRef = ref<{
  activateAndFocusFirstItem: () => void
} | null>(null)
const localePath = useLocalePath()
const isMobileMenuOpen = ref(false)
const isUserMenuOpen = ref(false)
const mobileMenuRef = ref<HTMLElement>()
const { hasTenantLogo } = useTenantLogo()

const toggleCollapse = () => {
  emit('update:isCollapsed', !isCollapsed)
  closeMobileMenu()
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

const mobileMenuButtonRef = ref<HTMLElement>()
onClickOutside(mobileMenuRef, closeMobileMenu, {
  ignore: [mobileMenuButtonRef],
})

const handleUserMenuItemClick = () => {
  closeMobileMenu()
}

const { activateAndFocusFirstItem } = useKeyboardNavigation({
  containerRef: navigationContainerRef,
  itemSelector:
    '[data-testid="nav-item"], [data-testid="user-menu-button"], [data-testid="desktop-collapse-button"]',
  rovingTabIndex: true,
  trapFocus: true,
  isActive: computed(() => !isUserMenuOpen.value),
  onSelected: (element: HTMLElement) => {
    element.click()
  },
  onSubmenuOpen: (element: HTMLElement) => {
    if (element.dataset.testid === 'user-menu-button') {
      element.click()
    } else {
      const event = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
      })
      element.dispatchEvent(event)
    }
  },
  onClose: () => {
    if (navigationItemsRef.value) {
      navigationItemsRef.value.handleCloseSubmenu()
    }
  },
})

useEventListener('keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isMobileMenuOpen.value) {
    closeMobileMenu()
  }
})

defineExpose({ activateAndFocusFirstItem })

const currentYear = computed(() => new Date().getFullYear())
const copyrightText = computed(() => {
  const baseText = `© ${currentYear.value}`
  return companyName ? `${baseText} ${companyName}` : baseText
})
</script>

<template>
  <div
    class="relative flex h-screen overflow-hidden"
    data-testid="navigation-drawer-container"
  >
    <!-- Mobile Header -->
    <div
      class="bg-white fixed left-0 right-0 top-0 z-50 border-b border-accent-06 lg:hidden"
      data-testid="mobile-header"
      aria-label="Mobile Navigation Header"
    >
      <div class="flex items-center justify-between gap-6 p-4">
        <AppLogo
          v-if="hasTenantLogo"
          size="sm"
          color="primary"
          data-testid="mobile-logo"
          aria-label="Company Logo"
        />
        <button
          ref="mobileMenuButtonRef"
          class="ml-auto text-accent-02 focus:outline-none"
          role="switch"
          :aria-checked="isMobileMenuOpen"
          aria-label="Toggle mobile menu"
          data-testid="mobile-menu-button"
          @click.stop="toggleMobileMenu"
        >
          <AppIcon
            :name="
              isMobileMenuOpen
                ? 'heroicons-outline:x'
                : 'heroicons-outline:menu'
            "
            size="md"
            color="basics-icon"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>

    <!-- Mobile Menu Panel -->
    <Transition
      enter-active-class="transition ease-in-out duration-300 transform"
      enter-from-class="-translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition ease-in-out duration-300 transform"
      leave-from-class="translate-x-0"
      leave-to-class="-translate-x-full"
    >
      <div
        v-if="isMobileMenuOpen"
        ref="mobileMenuRef"
        class="bg-white fixed inset-0 z-40 flex flex-col lg:hidden"
        data-testid="mobile-menu"
        aria-label="Mobile Navigation Menu"
        role="dialog"
        aria-modal="true"
      >
        <div class="flex-grow overflow-y-auto">
          <div class="mt-16 p-4">
            <AppNavigationItems
              :items="navigationItems"
              :is-collapsed="false"
              data-testid="mobile-navigation-items"
              @navigate="closeMobileMenu"
            />
          </div>
        </div>
        <div
          class="flex-shrink-0 border-t border-accent-06 p-4"
          data-testid="mobile-footer"
        >
          <AppUserMenu
            v-if="userName || userImage"
            :user-name="userName"
            :user-image="userImage"
            :user-mail="userMail"
            :is-collapsed="false"
            :menu-items="userMenuItems"
            data-testid="mobile-user-menu"
            @update:is-open="isUserMenuOpen = $event"
            @item-click="handleUserMenuItemClick"
          />
          <div
            class="mt-4 text-sm text-accent-04"
            data-testid="mobile-footer-links"
          >
            <div class="mb-2 flex flex-wrap gap-x-2 gap-y-1">
              <template v-for="(link, index) in footerLinks" :key="link.to">
                <NuxtLink
                  :to="localePath(link.to)"
                  :external="link.to.startsWith('http')"
                  class="hover:text-accent-03"
                  :aria-label="link.label"
                  :data-testid="`mobile-footer-link-${index}`"
                  @click="closeMobileMenu"
                >
                  {{ link.label }}
                </NuxtLink>
                <span
                  v-if="index < footerLinks.length - 1"
                  class="text-accent-05"
                  aria-hidden="true"
                  >|</span
                >
              </template>
            </div>
            <div data-testid="mobile-copyright">{{ copyrightText }}</div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Desktop Drawer -->
    <div
      ref="navigationContainerRef"
      :class="[
        'bg-white hidden h-full flex-col transition-all duration-300 ease-in-out lg:flex',
        isCollapsed ? 'w-[72px]' : 'w-[265px]',
      ]"
      data-testid="desktop-drawer"
      role="navigation"
      aria-label="Main Navigation"
    >
      <!-- Logo Container -->
      <div
        :class="[
          'flex flex-shrink-0 items-center justify-center pb-5 pt-10 transition-all duration-300',
          isCollapsed ? 'px-3' : 'px-5',
        ]"
        data-testid="desktop-header"
      >
        <AppLogo
          v-if="hasTenantLogo"
          size="md"
          color="primary"
          data-testid="desktop-logo"
          aria-label="Company Logo"
        />
      </div>

      <!-- Navigation Items -->
      <AppNavigationItems
        ref="navigationItemsRef"
        :items="navigationItems"
        :is-collapsed="isCollapsed"
        data-testid="desktop-navigation-items"
        @navigate="(item) => item.to && $router.push(localePath(item.to))"
      />

      <!-- Footer Section -->
      <div
        :class="[
          'flex-shrink-0 py-5 transition-all duration-300',
          isCollapsed ? 'px-3' : 'px-5',
        ]"
        data-testid="desktop-footer"
      >
        <div>
          <AppUserMenu
            v-if="userName || userImage"
            ref="desktopUserMenuRef"
            :user-name="userName"
            :user-image="userImage"
            :user-mail="userMail"
            :is-collapsed="isCollapsed"
            :menu-items="userMenuItems"
            data-testid="desktop-user-menu"
            @update:is-open="isUserMenuOpen = $event"
          />
          <div
            :class="[
              'mt-4 flex items-center',
              isCollapsed ? 'justify-center' : 'justify-end',
            ]"
            data-testid="desktop-footer-actions"
          >
            <!-- Collapse Icon -->
            <button
              :class="[
                'flex h-10 w-full items-center rounded-lg transition-colors duration-200 focus:bg-primary-50 focus:outline-none focus:ring-[3px] focus:ring-black',
                isCollapsed ? 'justify-center' : 'justify-end',
              ]"
              class=""
              data-testid="desktop-collapse-button"
              aria-label="Toggle navigation collapse"
              :aria-expanded="!isCollapsed"
              @click="toggleCollapse"
            >
              <span class="flex h-10 w-10 items-center justify-center">
                <AppIcon
                  name="fa6-solid:arrow-left"
                  size="sm"
                  color="basics-icon"
                  :class="[
                    'transition-transform duration-200',
                    isCollapsed ? 'rotate-180' : 'rotate-0',
                  ]"
                  data-testid="collapse-icon"
                  aria-hidden="true"
                />
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div
      class="flex-grow overflow-auto pt-16 lg:pt-0"
      data-testid="main-content"
      role="main"
    >
      <slot />
    </div>
  </div>
</template>
